/**
 * The theme picker. Account settings, Appearance.
 *
 * WHY TILES AND NOT THREE RADIO LABELS. A theme is the one preference in a
 * console whose whole meaning is what it looks like, and a word cannot carry
 * that. So each option draws the console: the rail, a health band with a real
 * lamp in it, two ledger rows, and the ink block. Nina has never deployed
 * anything and has no idea what "Night" is going to do to her screen; she can
 * see it before she picks it.
 *
 * A PREVIEW THAT CANNOT LIE. The tile is not a picture and it is not a hand
 * matched palette. It carries data-direction and data-theme on its own
 * wrapper, and the token layer paints it: the same direction primitives and
 * the same semantic mappings the product runs on. Change a token and the tile
 * changes with it. This is the entire reason semantic.css scopes its theme
 * blocks by attribute rather than by :root, and the reason it was worth doing.
 *
 * SELECTION IS A CHECK AND A WORD. Never a colored border on its own. The tile
 * that is chosen says "Selected" and carries a check mark, so the state
 * survives with every color turned off, which is the same rule the status
 * lamps live under.
 *
 * NOTHING IN A TILE IS A CONTROL. The small ink block inside the drawing is a
 * rectangle, not a Button: the doctrine's one solid control per screen counts
 * controls, and a drawing of a console is a drawing. The whole tile is one
 * radio, and the drawing inside it is aria-hidden.
 *
 * THE HONESTY RULE, which is the reason this component takes the direction as
 * an input at all: a direction that has no day palette gets one tile and a
 * sentence, not a Day option that would paint a screen nobody has designed and
 * not a "sync with system" that would answer Night no matter what the system
 * said. This is the same rule the demo dock already follows.
 */

import { useRef } from 'react';

import { t } from '../../lib/i18n/t';
import { useAppearance, type ThemeChoice } from '../../lib/hooks/use-theme';
import { captureEvent } from '../../lib/analytics/posthog';
import type { Direction, Theme } from '../../lib/types';

import { Lamp } from './StatusPill';
import '../../styles/components/theme-picker.css';

interface Option {
  value: ThemeChoice;
  /** The English string. It goes through t() at render, never before. */
  label: string;
}

const OPTIONS: Option[] = [
  { value: 'system', label: 'Sync with system' },
  { value: 'night', label: 'Night' },
  { value: 'day', label: 'Day' },
];

/**
 * The console, at a sixth of the size. The real Lamp component is used rather
 * than a drawn circle, so the one colored thing in the tile is the one colored
 * thing in the product.
 */
function Preview({ direction, theme }: { direction: Direction; theme: Theme }) {
  return (
    <span
      className="tpick__preview"
      data-direction={direction}
      data-theme={theme}
      aria-hidden="true"
    >
      <span className="tpick__rail">
        <span className="tpick__brand" />
        <span className="tpick__nav" data-on="yes" />
        <span className="tpick__nav" />
        <span className="tpick__nav" />
      </span>
      <span className="tpick__stage">
        <span className="tpick__band">
          <Lamp value="live" size="sm" />
          <span className="tpick__word" />
        </span>
        <span className="tpick__row" />
        <span className="tpick__row" />
        <span className="tpick__ink" />
      </span>
    </span>
  );
}

function Check() {
  return (
    <svg
      className="tpick__check"
      viewBox="0 0 16 16"
      width="12"
      height="12"
      focusable="false"
      aria-hidden="true"
    >
      <path
        d="M3 8.5 6.5 12 13 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ThemePicker() {
  const { choice, setChoice, direction, prefersDark, shipsDay } = useAppearance();
  const groupRef = useRef<HTMLDivElement | null>(null);

  // A direction with one palette has no choice in it. Rendering a radio group
  // of one would be furniture, so the tile is shown as what it is: the look,
  // stated. The stored choice is deliberately left alone, so flipping the
  // direction back in the demo dock restores what the person actually picked.
  if (!shipsDay) {
    return (
      <div className="tpick">
        <div className="tpick__grid" data-count="one">
          <div className="tpick__tile" data-static="yes">
            <Preview direction={direction} theme="night" />
            <span className="tpick__name">{t('Night')}</span>
            <span className="tpick__mark">
              <Check />
              {t('Selected')}
            </span>
          </div>
        </div>
        <p className="tpick__say">
          {t('This look ships in one theme. Night is the only palette it has, so there is nothing to pick.')}
        </p>
      </div>
    );
  }

  function move(delta: number) {
    const index = OPTIONS.findIndex((option) => option.value === choice);
    const next = OPTIONS[(index + delta + OPTIONS.length) % OPTIONS.length];
    setChoice(next.value);
    const tiles = groupRef.current?.querySelectorAll<HTMLButtonElement>('.tpick__tile');
    tiles?.[OPTIONS.indexOf(next)]?.focus();
  }

  return (
    <div className="tpick">
      {/* A radio group, not three buttons: one tab stop for the whole
          preference, arrows between the options, and a screen reader that
          says "Night, selected, two of three". */}
      <div
        className="tpick__grid"
        data-count="three"
        role="radiogroup"
        aria-label={t('Theme')}
        aria-describedby="tpick-say"
        ref={groupRef}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            move(1);
          }
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            move(-1);
          }
        }}
      >
        {OPTIONS.map((option) => {
          const selected = option.value === choice;
          // What this tile is going to paint. Sync draws whichever palette the
          // operating system is asking for right now, so the tile changes when
          // the system does, which is the promise the option is making.
          const shown: Theme =
            option.value === 'system' ? (prefersDark ? 'night' : 'day') : option.value;

          return (
            <button
              key={option.value}
              type="button"
              className="tpick__tile"
              role="radio"
              aria-checked={selected}
              // Roving tabindex. Three tab stops for one preference is three
              // stops a keyboard user pays on every pass down the page.
              tabIndex={selected ? 0 : -1}
              onClick={() => {
                captureEvent('theme_changed', { theme: option.value });
                setChoice(option.value);
              }}
            >
              <Preview direction={direction} theme={shown} />
              <span className="tpick__name">{t(option.label)}</span>
              <span className="tpick__mark">
                {selected ? (
                  <>
                    <Check />
                    {t('Selected')}
                  </>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      <p className="tpick__say" id="tpick-say">
        {prefersDark
          ? t('Your system is asking for a dark screen right now, so Sync with system means Night.')
          : t('Your system is asking for a light screen right now, so Sync with system means Day.')}
      </p>
    </div>
  );
}
